/*
 * The admin list of release notes (S42, 2026-10-12).
 *
 * ArticleListPage, copied and simplified: one row per note, which is what
 * kb.ReleaseNoteDetail returns, with the three-gate verdict and the separate
 * "draft" marker for a revision sitting ahead of the published one. The owner
 * asked for release notes to be "handled similar to Articles" in the admin, so
 * the filters, the URL state and the row shape are the article list's - every
 * view is a link - less what a release note does not have:
 *
 *   - no category filter and no "Untagged" state: a release note has a product
 *     and nothing else to tag. The state options are typed against
 *     AdminReleaseNoteStatusFilter, so offering one the server cannot parse
 *     fails the build.
 *   - the row names the note by title, version and product rather than by slug:
 *     the version and the product are its address.
 *
 * Delete goes through the server-driven impact dialog with the kind
 * 'release-note' and the label the server uses too (releaseNoteLabel: product
 * and version, e.g. "accoPLANNING v1.0.87.3"), so the typed confirmation, when
 * one is asked for, is the same string at both ends.
 *
 * Its words come from the chosen language's dictionary, `t.admin.releaseNotes` (S48).
 */

import { Link, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import type {
  AdminReleaseNoteListItem,
  AdminReleaseNoteSort,
  AdminReleaseNoteStatusFilter,
  DeleteResult,
} from '@accotool-kb/shared';
import { adminNewReleaseNoteHref, adminReleaseNoteHref, releaseNoteLabel } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAsync, useDebounced } from '../hooks/useAsync.js';
import {
  DateTime,
  EmptyState,
  ErrorBanner,
  Loading,
  Pagination,
  PublishingStatusBadge,
  Tag,
} from '../components/ui.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';
import { useMessages } from '../i18n/language.js';

const PAGE_SIZE = 20;

/**
 * The saved views, without the article list's "Untagged". `satisfies` ties the
 * values to the shared release-note filter list. Each one's label is the
 * dictionary's, by its value (`t.admin.releaseNotes.list.statuses`).
 */
export const RELEASE_NOTE_STATUS_OPTIONS = [
  { value: 'all' },
  { value: 'live' },
  { value: 'scheduled' },
  { value: 'hidden' },
  { value: 'draft' },
  { value: 'withdrawn' },
  { value: 'unwritten' },
] as const satisfies ReadonlyArray<{ value: AdminReleaseNoteStatusFilter }>;

const SORTS = ['modified', 'created', 'publish', 'title'] as const satisfies readonly AdminReleaseNoteSort[];

function parseIds(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isInteger(id) && id > 0);
}

/** /admin/releasenotes - every release note in every state, filtered from the URL. */
export function ReleaseNoteListPage(): JSX.Element {
  const t = useMessages();
  const words = t.admin.releaseNotes;
  const api = useAdminApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  // The result rather than the sentence, so the notice follows the language.
  const [notice, setNotice] = useState<DeleteResult | null>(null);

  const metadata = useAsync(() => api.metadata(), []);

  const statusParam = searchParams.get('status');
  const status: AdminReleaseNoteStatusFilter = RELEASE_NOTE_STATUS_OPTIONS.some(
    (option) => option.value === statusParam,
  )
    ? (statusParam as AdminReleaseNoteStatusFilter)
    : 'all';

  const products = parseIds(searchParams.get('products'));
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const sortParam = searchParams.get('sort');
  const sort: AdminReleaseNoteSort = SORTS.some((value) => value === sortParam)
    ? (sortParam as AdminReleaseNoteSort)
    : 'modified';

  const [searchDraft, setSearchDraft] = useState(searchParams.get('q') ?? '');
  const search = useDebounced(searchDraft, 350);

  const update = (changes: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next);
  };

  const filter = useMemo(
    () => ({ q: search, products, status, page, pageSize: PAGE_SIZE, sort }),
    [search, products.join(','), status, page, sort],
  );

  const state = useAsync((signal) => api.listReleaseNotes(filter, signal), [filter]);

  const onDeleted = (result: DeleteResult) => {
    setNotice(result);
    state.reload();
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{words.common.releaseNotes}</h1>
          <p>{state.data ? words.list.matching(state.data.total) : t.common.loading}</p>
        </div>
        <Link className="button button-primary" to={adminNewReleaseNoteHref()}>
          {words.common.newReleaseNote}
        </Link>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">
            {/* The steps are the server's own sentences, shown as they came. */}
            {words.list.deleted(
              notice.targetLabel,
              notice.performed.map((step) => step.description).join(' '),
            )}
          </div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="field toolbar-grow">
          <label htmlFor="q">{words.list.search}</label>
          <input
            id="q"
            type="search"
            placeholder={words.list.searchPlaceholder}
            value={searchDraft}
            onChange={(event) => {
              setSearchDraft(event.target.value);
              update({ q: event.target.value || null });
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="status">{words.list.state}</label>
          <select
            id="status"
            value={status}
            onChange={(event) => update({ status: event.target.value })}
          >
            {RELEASE_NOTE_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {words.list.statuses[option.value]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="product">{words.list.product}</label>
          <select
            id="product"
            value={products[0] ?? ''}
            onChange={(event) => update({ products: event.target.value || null })}
          >
            <option value="">{words.list.anyProduct}</option>
            {(metadata.data?.products ?? []).map((product) => (
              <option key={product.productId} value={product.productId}>
                {product.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="sort">{words.list.sort}</label>
          <select id="sort" value={sort} onChange={(event) => update({ sort: event.target.value })}>
            <option value="modified">{words.list.sorts.modified}</option>
            <option value="created">{words.list.sorts.created}</option>
            <option value="publish">{words.list.sorts.publish}</option>
            <option value="title">{words.list.sorts.title}</option>
          </select>
        </div>
      </div>

      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {state.loading && !state.data && <Loading label={words.list.loading} />}

      {state.data && state.data.items.length === 0 && (
        <EmptyState title={words.list.noneMatch}>
          <p>{words.list.noneMatchHint}</p>
        </EmptyState>
      )}

      {state.data && state.data.items.length > 0 && (
        <>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{words.list.columnReleaseNote}</th>
                    <th scope="col">{words.list.columnState}</th>
                    <th scope="col">{words.list.columnPublish}</th>
                    <th scope="col">{words.list.columnEdited}</th>
                    <th scope="col">
                      <span className="visually-hidden">{words.list.columnActions}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.items.map((note) => (
                    <ReleaseNoteRow
                      key={note.releaseNoteId}
                      note={note}
                      onDelete={() =>
                        setDeleteTarget({
                          kind: 'release-note',
                          targetId: note.releaseNoteId,
                          label: releaseNoteLabel(note.product.name, note.version),
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={state.data.total}
            onChange={(nextPage) => update({ page: String(nextPage) }, false)}
          />
        </>
      )}

      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={onDeleted}
      />
    </>
  );
}

function ReleaseNoteRow({
  note,
  onDelete,
}: {
  note: AdminReleaseNoteListItem;
  onDelete: () => void;
}): JSX.Element {
  const words = useMessages().admin.releaseNotes;
  const href = adminReleaseNoteHref(note.releaseNoteId);
  return (
    <tr>
      <td>
        <Link to={href} style={{ fontWeight: 600 }}>
          {note.title ?? words.common.untitled}
        </Link>
        <div className="faint">
          <code style={{ fontSize: '0.75rem' }}>{note.version}</code>
        </div>
        <div className="tag-list" style={{ marginTop: 4 }}>
          {/* One product, never none: kb.ReleaseNote.ProductId is NOT NULL. */}
          <Tag kind="product">{note.product.name}</Tag>
        </div>
      </td>

      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <PublishingStatusBadge status={note.status} noun="release note" />
          {note.hasUnpublishedDraft && (
            <span
              className="badge badge-draft"
              title={words.list.draftNewer(note.latestRevisionNumber, note.currentRevisionNumber)}
            >
              {words.list.draft}
            </span>
          )}
        </div>
      </td>

      <td className="faint">
        <DateTime value={note.publishAtUtc} />
      </td>

      <td className="faint">
        <DateTime value={note.modifiedAtUtc} />
      </td>

      <td className="actions">
        <Link className="button button-small" to={href}>
          {words.list.edit}
        </Link>{' '}
        <button type="button" className="button button-small button-quiet" onClick={onDelete}>
          {words.list.delete}
        </button>
      </td>
    </tr>
  );
}
